/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat poll RESULT CARD — Figma 7831:8581 ("Created Poll sent in chat").
// Two colour variants, selected by `isSelf`:
//   - Others (received): card `#15171C`, radio/bar accent `#537EFF`, bar
//     track `#222429`, avatar border `#15171C`, divider `#222429`.
//   - User (own):        card `#537EFF`, radio/bar accent `#FFFFFF`/`#E8EAED`,
//     bar track `#FFFFFF@40`, avatar border `#537EFF`, divider `#F4F4F5@08`.
// Shared in both: title `#F5F6F8` 16/700, subtitle `#FFFFFF@70` 12, option
// label `#F5F6F8` 16, voter count `#FFFFFF@70` 12, timestamp `#FFFFFF@40` 10,
// "View votes" `#F5F6F8` 16 with a list glyph + arrow (`#E8EAED`).
//
// Each option row: a 20×20 radio, the label + right-aligned voter-avatar
// stack (20×20 r4, −5 overlap) + count, and a 5px progress bar under the
// label whose fill is the option's share of the LEADING option.
//
// Presentation-only. The HOST owns the live vote projection + `onVote` /
// `onViewVotes`. className-first (light-token / `dark:`-Figma-hex hybrid,
// so the received variant renders in BOTH light and dark; the self/blue
// variant keeps the brand fill in both); the only inline value left is
// the runtime bar `width`. Lucide glyph colours resolve via the
// class-aware `useColorScheme` (NEVER the react-native one).

import type React from "react";
import { Image, Pressable, Text, View } from "react-native";
import { Check, CheckCheck } from "lucide-react-native";
import { useColorScheme } from "../foundations/colorScheme";
import { useTileMaxWidth } from "../chat/tileConstraint";

/** A resolved voter for the avatar stack on an option row. */
export interface PollVoterAvatar {
	readonly initials: string;
	readonly avatarUrl?: string | undefined;
	/** Resolved display name — rendered in the "Poll votes" panel voter list. */
	readonly name?: string | undefined;
}

/**
 * The poll's static content, mirroring the decoded `com.ping.poll`
 * (`PollCreateBody`) plus host-resolved creator metadata. `pollTarget`
 * is the poll message's content hash — the same key the host passes to
 * `usePollVotes` so the projection and the card agree.
 */
export interface PollViewContent {
	/** Content hash of the `com.ping.poll` message (vote OR-set anchor). */
	readonly pollTarget: string;
	readonly question: string;
	readonly options: readonly string[];
	/** When true, voters may pick several options. */
	readonly multipleChoice: boolean;
	/**
	 * When true the poll is anonymous — the per-option voter avatar stack is
	 * suppressed (counts only), and the "Poll votes" panel hides voter names.
	 */
	readonly anonymous?: boolean;
	/** Epoch ms the poll closes; omit/undefined for an open-ended poll. */
	readonly closesAt?: number;
	/** Resolved display name of the poll creator. */
	readonly creatorName: string;
	/** Optional avatar URL for the creator. */
	readonly creatorAvatarUrl?: string;
	/** Epoch ms the poll was created. */
	readonly createdAt: number;
}

export interface PollViewProps {
	readonly poll: PollViewContent;
	/** `optionIndex -> voter count`, from `usePollVotes().counts`. */
	readonly counts: Record<number, number>;
	/** The viewer's chosen option index, from `usePollVotes().myVote`. Drives
	 *  selection for SINGLE-choice polls (radio). */
	readonly myVote?: number;
	/** The viewer's FULL selected set, from `usePollVotes().myVotes`. Drives
	 *  selection for MULTI-select polls (checkboxes); ignored for single. */
	readonly myVotes?: readonly number[];
	/** Total distinct voters, from `usePollVotes().total`. */
	readonly total: number;
	/** Emits a vote for `optionIndex`; host sends `com.ping.poll_vote`. */
	readonly onVote: (optionIndex: number) => void;
	/** Resolved voter avatars per option (host-supplied) for the stack. */
	readonly votersByOption?: Record<number, ReadonlyArray<PollVoterAvatar>>;
	/** Opens the "Poll votes" panel (Figma 3466:10306). */
	readonly onViewVotes?: () => void;
	/** "now" injection for deterministic tests; defaults to Date.now(). */
	readonly now?: number;
	/**
	 * Own (sent-by-me) poll → the blue Figma "User" variant. Received →
	 * the dark "Others" variant. The host passes `message.isSelf`.
	 */
	readonly isSelf?: boolean;
	/** When the message is read (own polls) — renders the double-check. */
	readonly delivered?: boolean;
}

export const PollView: React.FC<PollViewProps> = ({
	poll,
	counts,
	myVote,
	myVotes,
	onVote,
	votersByOption,
	onViewVotes,
	now,
	isSelf = false,
	delivered = false,
}) => {
	const nowMs = now ?? Date.now();
	const closed = poll.closesAt !== undefined && poll.closesAt <= nowMs;
	const scheme = useColorScheme();
	// Thread side panel: cap the tile to the provided width.
	const tileMax = useTileMaxWidth();

	// Per-variant Tailwind class fragments (Figma 7831:8581). The self
	// (blue) variant keeps the brand fill across light + dark; the
	// received variant uses the hybrid light-token / dark-Figma-hex
	// pattern so the card renders correctly in BOTH colour schemes.
	const v = isSelf
		? {
				card: "bg-[#537EFF]",
				radioSelOuter: "bg-white",
				radioSelDot: "bg-[#537EFF]",
				radioRing: "border-white/40",
				radioRingFill: "",
				track: "bg-white/40",
				fill: "bg-[#E8EAED]",
				avatarBorder: "border-[#537EFF]",
				divider: "bg-[#F4F4F5]/[0.08]",
				title: "text-[#F5F6F8]",
				subtitle: "text-white/70",
				label: "text-[#F5F6F8]",
				count: "text-white/70",
				ts: "text-white/40",
				footer: "text-[#F5F6F8]",
			}
		: {
				card: "bg-white dark:bg-[#15171C]",
				radioSelOuter: "bg-[#537EFF]",
				radioSelDot: "bg-white",
				radioRing: "border-light-surface-tertiary dark:border-[#2F333B]",
				radioRingFill: "bg-white dark:bg-[#15171C]",
				track: "bg-light-surface-tertiary dark:bg-[#222429]",
				fill: "bg-[#537EFF]",
				avatarBorder: "border-white dark:border-[#15171C]",
				divider: "bg-light-surface-tertiary dark:bg-[#222429]",
				title: "text-light-text-primary dark:text-[#F5F6F8]",
				subtitle: "text-light-text-secondary dark:text-white/70",
				label: "text-light-text-primary dark:text-[#F5F6F8]",
				count: "text-light-text-secondary dark:text-white/70",
				ts: "text-light-text-tertiary dark:text-white/40",
				footer: "text-light-text-primary dark:text-[#F5F6F8]",
			};

	// Lucide CheckCheck takes a color string (no `dark:` class). Self →
	// translucent-white on the blue card; received → theme-resolved.
	const tsCheckColor = isSelf
		? "rgba(255,255,255,0.40)"
		: scheme === "dark"
			? "rgba(255,255,255,0.40)"
			: "rgba(0,0,0,0.40)";

	// Multi-select checkbox glyph colour = the filled square's inner tint
	// (`radioSelDot`): blue on the white self-square, white on the blue
	// received-square.
	const checkboxGlyphColor = isSelf ? "#537EFF" : "#FFFFFF";

	// Bars are sized relative to the LEADING option so the winner reads
	// as full-width (matching Figma), not relative to the grand total.
	const maxCount = poll.options.reduce(
		(m, _o, i) => Math.max(m, counts[i] ?? 0),
		0,
	);

	return (
		<View
			// `mt-xs` (not `my-xs`): a bottom margin pushes the bubble tail
			// (`::after` on the inline-block wrapper) ~4px below the card. Top-only
			// margin keeps row spacing while the tail attaches flush.
			className={`w-[280px] max-w-full mt-xs p-3 rounded-2xl ${v.card}`}
			style={tileMax !== null ? { maxWidth: tileMax } : undefined}
			accessibilityLabel={`Poll: ${poll.question}`}
		>
			{/* Question + subtitle */}
			<View className="px-1">
				<Text
					className={`text-base font-bold ${v.title}`}
					numberOfLines={4}
				>
					{poll.question}
				</Text>
				<Text className={`text-xs leading-[17px] ${v.subtitle}`}>
					{poll.multipleChoice ? "Select one or more" : "Select one"}
				</Text>
			</View>

			{/* Option rows */}
			<View className="mt-1 gap-1">
				{poll.options.map((option, index) => {
					const optionVotes = counts[index] ?? 0;
					const fraction = maxCount > 0 ? optionVotes / maxCount : 0;
					const pct = Math.round(fraction * 100);
					// Multi-select selection is set-membership in `myVotes`; single-choice
					// is the single `myVote` index.
					const selected = poll.multipleChoice
						? (myVotes ?? []).includes(index)
						: myVote === index;
					// Anonymous polls never reveal who voted — suppress the avatar
					// stack entirely (the raw count still renders).
					const voters = poll.anonymous ? [] : votersByOption?.[index] ?? [];
					const shown = Math.min(4, voters.length);
					// With avatars: show "+N" only for voters beyond the shown
					// avatars. Without avatars (host didn't resolve them): show
					// the raw count so a single vote reads "1", never "+1".
					const overflow = optionVotes - shown;
					const countLabel =
						voters.length > 0 ? (overflow > 0 ? `+${overflow}` : "") : `${optionVotes}`;
					return (
						<Pressable
							key={`${poll.pollTarget}:${index}`}
							accessibilityRole="button"
							accessibilityState={{ selected, disabled: closed }}
							disabled={closed}
							onPress={() => onVote(index)}
							className="flex-row items-center gap-2 px-1.5 py-1.5 rounded-xl active:opacity-80"
						>
							{/* Selection control (20×20): a rounded-SQUARE checkbox for
							    multi-select, a radio circle for single-choice. */}
							{poll.multipleChoice ? (
								selected ? (
									<View
										className={`w-5 h-5 rounded-md items-center justify-center ${v.radioSelOuter}`}
									>
										<Check size={14} strokeWidth={3} color={checkboxGlyphColor} />
									</View>
								) : (
									<View
										className={`w-5 h-5 rounded-md border-[1.5px] ${v.radioRing} ${v.radioRingFill}`}
									/>
								)
							) : selected ? (
								<View
									className={`w-5 h-5 rounded-full items-center justify-center ${v.radioSelOuter}`}
								>
									<View className={`w-2 h-2 rounded-full ${v.radioSelDot}`} />
								</View>
							) : (
								<View
									className={`w-5 h-5 rounded-full border-[1.5px] ${v.radioRing} ${v.radioRingFill}`}
								/>
							)}

							{/* Option (label + bar) */}
							<View className="flex-1 min-w-0 gap-1">
								<View className="flex-row items-center">
									<Text
										className={`flex-1 text-base ${v.label}`}
										numberOfLines={1}
									>
										{option}
									</Text>
									{optionVotes > 0 ? (
										<View className="flex-row items-center gap-0.5 pl-2">
											<View className="flex-row items-center">
												{voters.slice(0, 4).map((p, i) =>
													p.avatarUrl !== undefined ? (
														<Image
															key={`${index}:av:${i}`}
															source={{ uri: p.avatarUrl }}
															className={`w-5 h-5 rounded-[4px] border ${v.avatarBorder} ${
																i === 0 ? "" : "-ml-[5px]"
															}`}
															accessibilityIgnoresInvertColors
														/>
													) : (
														<View
															key={`${index}:av:${i}`}
															className={`w-5 h-5 rounded-[4px] items-center justify-center border bg-[#B5BAC3] ${v.avatarBorder} ${
																i === 0 ? "" : "-ml-[5px]"
															}`}
														>
															<Text className="text-[8px] font-bold text-[#15171C]">
																{p.initials.slice(0, 2)}
															</Text>
														</View>
													),
												)}
											</View>
											<Text className={`ml-0.5 text-xs ${v.count}`}>
												{countLabel}
											</Text>
										</View>
									) : null}
								</View>

								{/* Progress bar (track h5, rounded, fill = share) */}
								<View
									className={`h-[5px] rounded-full overflow-hidden ${v.track}`}
								>
									<View
										className={`h-full rounded-full ${v.fill}`}
										style={{ width: `${pct}%` }}
									/>
								</View>
							</View>
						</Pressable>
					);
				})}
			</View>

			{/* Timestamp */}
			<View className="flex-row items-center justify-end gap-0.5 mt-1 px-1">
				<Text className={`text-[10px] leading-[14px] ${v.ts}`}>
					{formatTime(poll.createdAt)}
				</Text>
				{isSelf && delivered ? (
					<CheckCheck size={12} strokeWidth={2} color={tsCheckColor} />
				) : null}
			</View>

			{/* Divider + View votes footer — Figma: plain centered text, no icons.
			    Only rendered when the host wired `onViewVotes`; otherwise the
			    affordance would be a dead no-op button (display-only surfaces). */}
			{onViewVotes !== undefined ? (
				<>
					<View className={`-mx-2 mt-1 h-px ${v.divider}`} />
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="View votes"
						onPress={onViewVotes}
						className="items-center justify-center pt-2.5 active:opacity-70"
					>
						<Text className={`text-base ${v.footer}`}>View votes</Text>
					</Pressable>
				</>
			) : null}
		</View>
	);
};

/** Short clock time for the timestamp (e.g. `3:07 PM`). */
function formatTime(epochMs: number): string {
	try {
		return new Date(epochMs).toLocaleTimeString([], {
			hour: "numeric",
			minute: "2-digit",
		});
	} catch {
		return "";
	}
}
